import { motion, AnimatePresence } from 'motion/react';
import { useMemo } from 'react';
import { useGetHealthData } from '../../use-health-data';
import { HealthDataCategory } from '@/api/generated-catch-backend/model';
import { LABS_CATEGORY_NAME } from '../../constants';

const CARD_BASE_WIDTH = 310;
const CARD_WIDTH_STEP = 16;
const STARTING_OPACITY = 0.25;
const MIN_OPACITY = 0.02;
const OPACITY_DECAY_FACTOR = 0.66;
const CARD_OFFSET = -39;

const calculateOpacity = (index: number): number => {
  if (index === 0) return STARTING_OPACITY;
  return Math.max(MIN_OPACITY, STARTING_OPACITY * Math.pow(OPACITY_DECAY_FACTOR, index));
};

interface CardBaseProps {
  opacity: number;
  width: number;
  index: number;
}

interface CategoryCardData extends CardBaseProps {
  category: HealthDataCategory;
}

function SkeletonCard({ opacity, width, index }: CardBaseProps) {
  return (
    <motion.div
      className="relative box-border flex h-[39px] shrink-0 items-center justify-between overflow-hidden rounded-[6px] px-[15px] py-[10px] text-white backdrop-blur-sm backdrop-filter"
      style={{
        backgroundColor: `rgba(124, 241, 199, ${opacity})`,
        width: `${width}px`,
        willChange: 'transform',
      }}
      initial={{
        opacity: 0,
        y: index === 0 ? 0 : CARD_OFFSET * index,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{
        opacity: { duration: 0.3, delay: index * 0.05 },
        y: { duration: 0.8, delay: 0.5 + index * 0.1, ease: 'easeOut' },
      }}
    >
      {/* Shimmer overlay */}
      <motion.div
        className="absolute inset-0 -translate-x-full"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent)',
        }}
        animate={{
          x: ['0%', '200%'],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'linear',
        }}
      />

      {/* Skeleton content */}
      <div className="h-[14px] w-[60%] rounded bg-white/30" />
      <div className="h-[12px] w-[30%] rounded bg-white/30" />
    </motion.div>
  );
}

interface CategoryCardProps extends CardBaseProps {
  category: HealthDataCategory;
}

function CategoryCard({ category, opacity, width }: CategoryCardProps) {
  return (
    <div
      className="box-border flex h-[39px] shrink-0 items-center justify-between rounded-[6px] px-[15px] py-[10px] text-white backdrop-blur-sm backdrop-filter"
      style={{
        backgroundColor: `rgba(124, 241, 199, ${opacity})`,
        width: `${width}px`,
      }}
    >
      <motion.p
        className="text-center text-[14px] leading-[1.4] font-bold"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {category.name}
      </motion.p>
      <motion.p
        className="text-right text-[12px] leading-[1.3] font-normal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {category.totalDataPoints} factors
      </motion.p>
    </div>
  );
}

export function HealthDataHero() {
  const { isFetching, healthData } = useGetHealthData();

  const categoryCards = useMemo((): CategoryCardData[] => {
    const categories =
      healthData?.categories ||
      Array.from({ length: 7 }, (_, i) => ({
        id: `skeleton-${i}`,
        name: '',
        lastUpdated: '',
        displayOrder: i,
        totalDataPoints: 0,
        populatedDataPoints: 0,
        subcategories: [],
      }));
    return [...categories]
      .sort((a, b) => {
        // Pin "Catch Labs" category to the bottom to de-emphasize
        // usually will have the most HDPs so will be seen first
        if (a.name === LABS_CATEGORY_NAME) return 1;
        if (b.name === LABS_CATEGORY_NAME) return -1;
        return b.totalDataPoints - a.totalDataPoints;
      })
      .map((category: HealthDataCategory, index: number) => ({
        category,
        opacity: calculateOpacity(index),
        width: CARD_BASE_WIDTH - index * CARD_WIDTH_STEP,
        index,
      }));
  }, [healthData?.categories]);

  return (
    <div className="relative flex flex-col items-center" data-name="chart-graphic">
      <div
        className="flex flex-col items-center"
        style={{
          height: '273px',
          overflow: 'hidden',
        }}
      >
        {categoryCards.map(({ category, opacity, width, index }) => (
          <div key={category.id} className="relative">
            <AnimatePresence mode="wait">
              {isFetching ? (
                <SkeletonCard key={`skeleton-${category.id}`} opacity={opacity} width={width} index={index} />
              ) : (
                <CategoryCard
                  key={`category-${category.id}`}
                  category={category}
                  opacity={opacity}
                  width={width}
                  index={index}
                />
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
}
